import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  apiClient,
  persistVendorBearerToken,
  setLogoutHandler,
  setVendorSessionToken,
} from "../services/apiClientService";

export const AuthContext = createContext();

/** Backend default for new vendors is `inactive` until admin sets `active`. */
export function canVendorAccessPortal(vendorInfo) {
  const s = vendorInfo?.status;
  if (!s) return false;
  return !["blocked", "deleted"].includes(s);
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = (vendorInfo, sessionToken = null) => {
    if (!canVendorAccessPortal(vendorInfo)) return;
    if (sessionToken) setVendorSessionToken(sessionToken);
    setIsAuthenticated(true);
    setVendor(vendorInfo);
    localStorage.setItem("vendor", JSON.stringify(vendorInfo));
    localStorage.setItem("isAuthenticated", "true");
  };

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await apiClient("POST", "/api/vendors/logout");
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setIsAuthenticated(false);
      setVendor(null);
      localStorage.removeItem("isAuthenticated");
      localStorage.removeItem("vendor");
      persistVendorBearerToken();
      setLoading(false);
    }
  }, []);

  const handleUnauthorized = useCallback(async () => {
    await logout();
    window.location.replace("/vendor/login");
  }, [logout]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await apiClient("GET", "/api/vendors/profile", null, {
          skipLoading: true,
        });
        if (cancelled) return;
        if (v && canVendorAccessPortal(v)) {
          setIsAuthenticated(true);
          setVendor(v);
          localStorage.setItem("vendor", JSON.stringify(v));
          localStorage.setItem("isAuthenticated", "true");
        } else {
          setIsAuthenticated(false);
          setVendor(null);
          localStorage.removeItem("isAuthenticated");
          localStorage.removeItem("vendor");
        }
      } catch {
        if (cancelled) return;
        setIsAuthenticated(false);
        setVendor(null);
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("vendor");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    setLogoutHandler("vendor", handleUnauthorized);
    return () => {
      cancelled = true;
    };
  }, [handleUnauthorized]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        vendor,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
